import { APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule, Type } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule, HttpClient, HTTP_INTERCEPTORS, HttpClientXsrfModule } from '@angular/common/http';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

// icons
import { TablerIconsModule } from 'angular-tabler-icons';
import * as TablerIcons from 'angular-tabler-icons/icons';

// perfect scrollbar
import { NgScrollbarModule } from 'ngx-scrollbar';

//Import all material modules
import { MaterialModule } from './material.module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
//Import Layouts
import { FullComponent } from './layouts/full/full.component';
import { BlankComponent } from './layouts/blank/blank.component';

import { FilterPipe } from './pipe/filter.pipe';

import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ApiService } from './services/api.service';
import { AppService } from './services/app.service';
import { AppInterceptor } from './app.interceptor';
import { SignalRService } from './services/signalr.service';
import { ActiveCashDeskInterceptor } from './@clu-fe/zcoreutils/active-cashdesk.interceptor';

import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import { initDatabase_CashDeskRxDb, CashDeskCoreRxDbService } from './@clu-fe/apps/m-cashdesk/@services/m-cashdesk-core-rxdb.service';
import { CashDeskSyncUpService } from './@clu-fe/apps/m-cashdesk/@services/m-cashdesk-syncup.service';
import { ApiCluAgentService } from './services/api-clu-agent.service';
import { CashdeskDevicesPrinterService } from './services/cashdesk-devices-printer.service';
import { AuthModule } from './@auth/auth.module';
import { AuthService } from './@auth/auth.service';
import { AccountService } from './services/account.service';
import { ProfileModule } from './pages/profile/profile.module';
import { CluLockscreenModule } from './utils/clu-lockscreen/clu-lockscreen.module';
import { CluPermissionModule } from './utils/clu-permission/clu-permission.module';
import { CluPublicModule } from './@public/public.module';
import { ReleaseNotesModule } from './pages/release-notes/release-notes.module';
import { initDatabase_RemDocsRxDb, RemDocsCoreRxDbService } from './@clu-fe/apps/m-remdocs/@services/m-remdocs-core-rxdb.service';
import { CashDeskZCluJetService } from './@clu-fe/apps/m-cashdesk/@services/m-cashdesk-zclujet.service';
import { BluetoothPrinterModule } from './utils/bluetooth-printer-utils/bluetooth-printer.module';
import { Router } from '@angular/router';
import * as Sentry from "@sentry/angular";
import { AppErrorHandler } from './utils/app-error-handler';
import { CluAgentModule } from './utils/clu-agent/clu-agent.module';
import { DynamicFormsBasicUIModule } from '@ng-dynamic-forms/ui-basic';
import { NgxMaskModule } from 'ngx-mask';
import { DYNAMIC_FORM_CONTROL_MAP_FN, DynamicFormControlModel, DynamicFormControl } from '@ng-dynamic-forms/core';
import { DynamicBtTextboxComponent } from './utils/bluetooth-printer-designer/textbox/dynamic-bt-textbox.component';
import { DynamicBtNumericTextboxComponent } from './utils/bluetooth-printer-designer/numeric-textbox/dynamic-bt-numeric-textbox.component';
import { DynamicBtDropdownComponent } from './utils/bluetooth-printer-designer/dropdown/dynamic-bt-dropdown.component';
import { DynamicBtTextareaComponent } from './utils/bluetooth-printer-designer/textarea/dynamic-bt-textarea.component';
import { DynamicBtLabelComponent } from './utils/bluetooth-printer-designer/label/dynamic-bt-label.component';
import { DynamicBtDatasourcesComponent } from './utils/bluetooth-printer-designer/datasources/dynamic-bt-datasources.component';
import { DynamicBtBarcodeComponent } from './utils/bluetooth-printer-designer/barcode/dynamic-bt-barcode.component';
import { DynamicBtImageComponent } from './utils/bluetooth-printer-designer/image/dynamic-bt-image.component';
import { DynamicBtFilePickerComponent } from './utils/bluetooth-printer-designer/file-picker/dynamic-bt-file-picker.component';
import { DynamicBtHSeparatorComponent } from './utils/bluetooth-printer-designer/hseparator/dynamic-bt-hseparator.component';
import { DynamicBtQRCodeComponent } from './utils/bluetooth-printer-designer/qrcode/dynamic-bt-qrcode.component';
import { CashDeskZCluZLogService } from './@clu-fe/apps/m-cashdesk/@services/m-cashdesk-zcluzlog.service';
import { CashDeskZCluZTickTraceService } from './@clu-fe/apps/m-cashdesk/@services/m-cashdesk-zcluzticktrace.service';
import { POrderPortailAppService } from './@clu-fe/apps/m-orderportail/@app/m-op-app.service';
import { OrderPortailInterceptor } from './@clu-fe/apps/m-orderportail/order-portail.interceptor';
import { BrowserCheckService } from './services/browser-check.service';
import { initDatabase_ZTicketVaultDb, TICKET_VAULT_PROVIDERS } from './@clu-fe/apps/m-cashdesk/@zticketvault/zticketvault-db.service';
import { ZTicketVaultDbDocumentDaoService } from './@clu-fe/apps/m-cashdesk/@zticketvault/zticketvault-db-document-dao.service';
import { TauriService } from './services/tauri/tauri.service';
import { TauriCashdeskDatasService } from './services/tauri/cashdesk-datas-tauri.service';
import { ConnectorCashDeskSyncDownDaoService } from './@clu-fe/apps/m-cashdesk/cashdesk-sync-down/connector-cashdesk-sync-down-dao.service';
registerLocaleData(localeFr, 'fr');

export function HttpLoaderFactory(http: HttpClient): any {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

function initApp(_appService: AppService, _apiService: ApiService, tauriService: TauriService, router: Router) {
  return () => new Promise<void>((resolve, reject) => {
    Promise.all([
      _appService.getAppVersion(),
    ]).then(async (results) => {
      if (tauriService.isTauri/* && _appService.appVersion$.value.APP_ENVIRONMENT == 'prod'*/) {
        const appInfos = _appService.appVersion$.value;
        if (appInfos) {
          const confirmUpdates = await tauriService.checkForUpdates(appInfos);
          if (confirmUpdates) {
            router.navigate(['/update-app']);
          }
        }
      }
      console.log("CAURIS LINKUP LOADED");
      resolve();
    }, (errors) => {
      console.log("FAILED TO LOAD CAURIS LINKUP");
      if (window.document.getElementById('loading-spinner') !== null) {
        window.document.getElementById('loading-spinner')!.style.display = 'none';
      }
      if (window.document.getElementById('loading-error') !== null) {
        window.document.getElementById('loading-error')!.style.display = 'block';
      }
      reject(errors);
    });
  });
}



@NgModule({
  declarations: [AppComponent, BlankComponent, FilterPipe],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    HttpClientXsrfModule.withOptions({
      cookieName: 'XSRF-TOKEN',
      headerName: 'X-XSRF-TOKEN'
    }),
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    NgxMaskModule.forRoot({}),
    MaterialModule,
    TablerIconsModule.pick(TablerIcons),
    NgScrollbarModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    FullComponent,
    AuthModule,
    ProfileModule,
    ReleaseNotesModule,
    CluLockscreenModule,
    CluPermissionModule,
    CluPublicModule,
    BluetoothPrinterModule,
    CluAgentModule,
  ],
  exports: [TablerIconsModule],
  bootstrap: [AppComponent],
  providers : [
    { provide: APP_INITIALIZER, multi : true, deps : [AppService, ApiService, TauriService, Router], useFactory : initApp},
    { provide: APP_INITIALIZER, multi: true, deps: [/* your dependencies */], useFactory: () => initDatabase_CashDeskRxDb, },
    { provide: APP_INITIALIZER, multi: true, deps: [/* your dependencies */], useFactory: () => initDatabase_RemDocsRxDb, },
    ...TICKET_VAULT_PROVIDERS,
    { provide: HTTP_INTERCEPTORS, useClass: AppInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ActiveCashDeskInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: OrderPortailInterceptor, multi: true },
    { provide: LOCALE_ID, useValue: 'fr' },
    AppService,
    AccountService,
    ApiService,
    AuthService,
    ApiCluAgentService,
    CashDeskSyncUpService,
    CashDeskCoreRxDbService,
    RemDocsCoreRxDbService,
    ZTicketVaultDbDocumentDaoService,
    CashdeskDevicesPrinterService,
    CashDeskZCluJetService,
    CashDeskZCluZLogService,
    CashDeskZCluZTickTraceService,
    ConnectorCashDeskSyncDownDaoService,

    SignalRService,

    BrowserCheckService,
    TauriService,
    TauriCashdeskDatasService,

    //{ provide: ErrorHandler, useClass: AppErrorHandler },
    //{ provide: ErrorHandler, useValue: Sentry.createErrorHandler(), },
    { provide: Sentry.TraceService, deps: [Router], },
    { provide: APP_INITIALIZER, useFactory: () => () => {}, deps: [Sentry.TraceService], multi: true, },

    {
      provide: DYNAMIC_FORM_CONTROL_MAP_FN,
      useValue: (model: DynamicFormControlModel): Type<DynamicFormControl> | null  => {
          switch (model.type) {
              case 'bt_textbox': return DynamicBtTextboxComponent;
              case 'bt_numeric_textbox': return DynamicBtNumericTextboxComponent;
              case 'bt_dropdown': return DynamicBtDropdownComponent;
              case 'bt_textarea': return DynamicBtTextareaComponent;
              case 'bt_label': return DynamicBtLabelComponent;
              case 'bt_datasources': return DynamicBtDatasourcesComponent;
              case 'bt_qrcode': return DynamicBtQRCodeComponent;
              case 'bt_barcode': return DynamicBtBarcodeComponent;
              case 'bt_image': return DynamicBtImageComponent;
              case 'bt_file_picker': return DynamicBtFilePickerComponent;
              case 'bt_h_separator': return DynamicBtHSeparatorComponent;
          }
          return null;
      },
    },
    POrderPortailAppService
  ]
})
export class AppModule {}
